import { inject, Injectable } from '@angular/core';
import {
  AdMob,
  AdmobConsentStatus,
  AdMobRewardItem,
  AdOptions,
  BannerAdOptions,
  BannerAdPluginEvents,
  BannerAdPosition,
  BannerAdSize,
  InterstitialAdPluginEvents,
  RewardAdOptions,
  RewardAdPluginEvents,
} from '@capacitor-community/admob';
import { DeviceState } from '../../shared/device-state/device.state';
import { Store } from '@ngxs/store';
import { environment } from '../../environments/environment';
import { UserStateActions } from '../../shared/user-state/user-state.actions';
import { UserState } from '../../shared/user-state/user.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BandmanagerRestApiAdsTypeEnum } from '@digitale-menschen/bandmanager-rest-api';
import { LoadingService } from './loading.service';
import { AnalyticsService } from './analytics.service';
import { SettingsState } from '../../shared/settings-state/settings-state.state';

@Injectable({
  providedIn: 'root',
})
export class AdsService {
  private activeAdsType: BandmanagerRestApiAdsTypeEnum | null = null;
  private adsEnabledForThisUser = true;
  private globAdsEnabled = false;
  private isInitialized = false;
  private analyticsService = inject(AnalyticsService);
  private store = inject(Store);
  private loadingService = inject(LoadingService);

  constructor() {
    // check User if he has bought/disabled ads,
    this.store.select(UserState.user).pipe(takeUntilDestroyed()).subscribe((user) => {
      this.adsEnabledForThisUser = !(user?.profile?.user.paymentForAds);
      if (!this.adsEnabledForThisUser) {
        this.removeBanner();
      }
    });

    this.store.select(SettingsState.settingByKey(environment.settingsKeys.globAds)).pipe(takeUntilDestroyed()).subscribe((setting) => {
      this.globAdsEnabled = setting.numbervalue === 1;
    });
  }

  public isAdsEnabled(): boolean {
    return this.globAdsEnabled ? this.adsEnabledForThisUser : false;
  }

  public async initialize(testing = false): Promise<void> {
    if (!this.adsEnabledForThisUser) {
      return;
    }

    let deviceId = '';
    if (testing) {
      deviceId = this.store.selectSnapshot(DeviceState.deviceInfos)?.uniqueIdentifier || '';
    }
    const payload = testing ? {
      initializeForTesting: true,
      testingDevices: [deviceId],
    } : { requestTrackingAuthorization: true };
    console.log('launching Admob.initialize', payload);
    await AdMob.initialize(payload);

    const [trackingInfo, consentInfo] = await Promise.all([
      AdMob.trackingAuthorizationStatus(),
      AdMob.requestConsentInfo(),
    ]);

    console.log('trackingInfo', trackingInfo);
    if (trackingInfo.status === 'notDetermined') {
      /**
       * If you want to explain TrackingAuthorization before showing the iOS dialog,
       * you can show the modal here.
       * ex)
       * const modal = await this.modalCtrl.create({
       *   component: RequestTrackingPage,
       * });
       * await modal.present();
       * await modal.onDidDismiss();  // Wait for close modal
       **/

      console.log('requesting tracking authorization');
      await AdMob.requestTrackingAuthorization();
    }

    const authorizationStatus = await AdMob.trackingAuthorizationStatus();
    if (
      authorizationStatus.status === 'authorized' &&
      consentInfo.isConsentFormAvailable &&
      consentInfo.status === AdmobConsentStatus.REQUIRED
    ) {
      await AdMob.showConsentForm();
    }

    this.isInitialized = true;
  }

  public async showAds(adsType: BandmanagerRestApiAdsTypeEnum): Promise<AdMobRewardItem | null> {
    if (!this.adsEnabledForThisUser || !this.globAdsEnabled) {
      return null;
    }

    if (!this.isInitialized) {
      await this.analyticsService.logEvent({
        name: 'adsService error',
        params: { message: 'bypassing ads: adsService not initialized yet' },
      });
      return null;
    }

    const adsId = this.getAdsIdForTypeAndPlatform(adsType);
    if (adsId === 'web') {
      this.processAdsCallback();
      return environment.defaultAdsReward;
    }
    switch (adsType) {
      case BandmanagerRestApiAdsTypeEnum.StaticLowerBanner:
        return this.showBanner('bottom', adsId);
      case BandmanagerRestApiAdsTypeEnum.RewardBandroomUpgrade:
      case BandmanagerRestApiAdsTypeEnum.RewardBandroomColorchange:
      case BandmanagerRestApiAdsTypeEnum.RewardUniformColorchange:
        this.activeAdsType = adsType;
        return this.showRewardVideo(adsId);
      case BandmanagerRestApiAdsTypeEnum.InterstitialBandroomUpgrade:
      case BandmanagerRestApiAdsTypeEnum.InterstitialEventParticipate:
      case BandmanagerRestApiAdsTypeEnum.InterstitialRehearsalStart:
      default:
        this.activeAdsType = adsType;
        return this.showInterstitial(adsId);
    }
  }

  // UMP
  public async showConsent() {
    const consentInfo = await AdMob.requestConsentInfo();

    if (consentInfo.isConsentFormAvailable && consentInfo.status === AdmobConsentStatus.REQUIRED) {
      const { status } = await AdMob.showConsentForm();
    }
  }

  public removeBanner(): void {
    AdMob.removeBanner().then();
  }

  private getAdsIdForTypeAndPlatform(adsType: BandmanagerRestApiAdsTypeEnum): string {
    let settingSuffix: 'android' | 'ios' = 'android';
    const platform = this.store.selectSnapshot(DeviceState.deviceInfos)?.platform;
    if (platform === 'web') {
      return 'web';
    } else if (platform === 'ios') {
      settingSuffix = 'ios';
    }
    return environment.adsTypes[adsType][settingSuffix];
  }

  private processAdsCallback(): void {
    if (this.activeAdsType !== BandmanagerRestApiAdsTypeEnum.StaticLowerBanner) {
      this.store.dispatch(new UserStateActions.incrementNumberAdsViewed());
    }
  }

  private async showBanner(position: 'middle' | 'bottom', adsId: string): Promise<AdMobRewardItem | null> {
    return new Promise(async (resolve) => {
      try {
        const bannerListener = await AdMob.addListener(BannerAdPluginEvents.AdImpression, () => {
          this.processAdsCallback();
          bannerListener.remove();
          resolve(environment.defaultAdsReward);
        });

        const options: BannerAdOptions = {
          adId: adsId,
          adSize: BannerAdSize.ADAPTIVE_BANNER,
          position: position === 'middle' ? BannerAdPosition.CENTER : BannerAdPosition.BOTTOM_CENTER,
          // if bottom, we set it on top of main menu
          margin: position === 'bottom' ? 80 : 0,
          isTesting: !environment.production,
          // npa: true
        };
        await AdMob.showBanner(options);
      } catch (error) {
        console.error('error by banner ads', error);
        resolve(environment.defaultAdsReward);
      }
    });
  }

  private async showInterstitial(adsId: string): Promise<AdMobRewardItem | null> {
    return new Promise(async (resolve) => {
      try {
        const interstitialListener = await AdMob.addListener(InterstitialAdPluginEvents.Showed, () => {
          this.processAdsCallback();
          interstitialListener.remove();
          resolve(environment.defaultAdsReward);
        });

        const options: AdOptions = {
          adId: adsId,
          isTesting: !environment.production,
          // npa: true
        };

        this.loadingService.display();
        const loadInfo = await AdMob.prepareInterstitial(options);
        if (loadInfo) {
          this.loadingService.hide();
          await AdMob.showInterstitial();
        } else {
          this.loadingService.hide();
          await this.analyticsService.logEvent({
            name: 'adsService PrepareInterstitial, no ad-unit',
            params: { message: 'adsService PrepareInterstitial, no ad-unit' },
          });
          resolve(environment.defaultAdsReward);
        }
      } catch (error) {
        console.error('error by interstitial ads', error);
        resolve(environment.defaultAdsReward);
      }
    });
  }

  private async showRewardVideo(adsId: string): Promise<AdMobRewardItem | null> {
    return new Promise(async (resolve) => {
      try {
        const rewardListener = await AdMob.addListener(RewardAdPluginEvents.Rewarded, (rewardData) => {
          this.processAdsCallback();
          rewardListener.remove();
          resolve(rewardData);
        });

        const options: RewardAdOptions = {
          adId: adsId,
          isTesting: !environment.production,
          // npa: true
          // ssv: {
          //   userId: "A user ID to send to your SSV"
          //   customData: JSON.stringify({ ...MyCustomData })
          //}
        };
        this.loadingService.display();
        await AdMob.prepareRewardVideoAd(options);
        this.loadingService.hide();
        await AdMob.showRewardVideoAd();
      } catch (error) {
        console.error('error by reward ads', error);
        resolve(environment.defaultAdsReward);
      }
    });
  }
}
